import { Box, Grid, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material'
import Skeleton from '@mui/material/Skeleton'
import { useTheme } from '@mui/material/styles'
import clsx from 'clsx'
import { APIError } from 'components/core/errors/api'
import { C1Icon } from 'components/core/icons/C1Icon'
import { Pagination, PaginationType } from 'components/core/pagination'
import { Subtitle, Title } from 'components/core/typeface'
import { ListReturn, Status } from 'lib/rpc'
import { CircleHelp } from 'lucide-react'
import { ReactNode, useState } from 'react'
import { useStyles } from './styles'

export type ListProps<T> = ListPropsWithTable | ListPropsWithoutTable<T>

export type ListCommonProps = {
    title?: string | React.ReactElement
    subtitle?: ReactNode
    titleVariant?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
    search?: React.ReactElement | React.ReactElement[]
    sidebar?: React.ReactElement | null
    additionalInfo?: ReactNode
    buttons?: React.ReactElement | React.ReactElement[]
}

export type ListPropsWithTable = {
    emptyText?: never
    emptyTextTooltip?: boolean
    listVariant?: 'standard' | 'simple'
    header?: never
    items?: never
    renderItem?: never
    onPageChange?: never
    paginationType?: never
    table: React.ReactElement
} & ListCommonProps

export type ListPropsWithoutTable<T> = {
    emptyText?: string | React.ReactElement
    emptyTextTooltip?: boolean
    listVariant?: 'standard' | 'simple'
    header?: React.ReactElement
    items: ListReturn<T> | T[]
    renderItem: (T, index?: number) => React.ReactElement
    onPageChange?: (pageToken: string) => void
    paginationType?: PaginationType
    table?: never
} & ListCommonProps

export const TableChrome = ({
    header,
    children,
    listVariant,
}: {
    header?: React.ReactElement
    children: ReactNode
    listVariant?: 'standard' | 'simple'
}): React.ReactElement => {
    const theme = useTheme()
    const classes = useStyles(theme)
    const tableHeadStyle = clsx({
        [classes.header]: listVariant !== 'simple',
    })
    const tableContainerStyle = clsx({
        [classes.container]: listVariant !== 'simple',
    })
    const tableRowStyle = clsx({
        [classes.row]: listVariant !== 'simple',
    })
    return (
        <TableContainer className={tableContainerStyle}>
            <Table stickyHeader>
                <TableHead className={tableHeadStyle}>{header}</TableHead>
                <TableBody className={tableRowStyle}>{children}</TableBody>
            </Table>
        </TableContainer>
    )
}

const ListTable = <T, >({
    emptyText,
    listVariant,
    items,
    header,
    renderItem,
    onPageChange,
    additionalInfo,
    paginationType = PaginationType.paged,
}: Pick<
    ListProps<T>,
    | 'emptyText'
    | 'listVariant'
    | 'items'
    | 'header'
    | 'renderItem'
    | 'onPageChange'
    | 'additionalInfo'
    | 'paginationType'
>): React.ReactElement | null => {
    const theme = useTheme()
    const classes = useStyles(theme)

    // NOTE(morgabra) This is really brittle as it's counting on referential integrity of the dispatched items list...
    //                We should probably convert these to immutable lists in reducers?
    const [itemsArray, setItemsArray] = useState<Array<T> | null>(null)

    const [currentPageToken, setCurrentPageToken] = useState<string>('')
    const [nextPageToken, setNextPageToken] = useState<string>('')
    const [previousPageTokens, setPreviousPageTokens] = useState<Array<string>>([])

    if (!renderItem || !items) {
        return null
    }

    const getTableContents = (tableItems): React.ReactElement => {
        if (tableItems && tableItems.length > 0) {
            return (
                <TableChrome header={header} listVariant={listVariant}>
                    {tableItems.map((item) => renderItem(item))}
                </TableChrome>
            )
        }
        const emptyTextClass = clsx({ [classes.simple]: listVariant === 'simple' })
        return (
            <TableChrome header={header} listVariant={listVariant}>
                <TableRow key="empty">
                    <TableCell colSpan={100} className={emptyTextClass}>{emptyText}</TableCell>
                </TableRow>
            </TableChrome>
        )
    }

    const contents = (): React.ReactElement => {
        // can pass a simple array of items or a ListReturn of items
        if (Array.isArray(items)) {
            if (items !== itemsArray) {
                setItemsArray(items)
            }
            return getTableContents(itemsArray)
        }
        switch (items.status) {
            case Status.ERROR:
                return (
                    <TableChrome header={header} listVariant={listVariant}>
                        <TableRow key="error">
                            <TableCell>
                                <div>
                                    <APIError err={items.error} />
                                </div>
                            </TableCell>
                        </TableRow>
                    </TableChrome>
                )
            case Status.INIT:
            case Status.LOADING:
                if (itemsArray === null || itemsArray.length === 0) {
                    return (
                        <TableChrome header={header} listVariant={listVariant}>
                            <TableRow key="loading">
                                <TableCell>
                                    <Skeleton />
                                    <Skeleton animation={false} />
                                    <Skeleton animation="wave" />
                                </TableCell>
                            </TableRow>
                        </TableChrome>
                    )
                }
            // falls through intentinoally to keep already loaded itemsArray
            case Status.OK:
                if (items.status === Status.OK && items.items !== itemsArray) {
                    if (onPageChange) {
                        const metadata = items.metadata
                        setCurrentPageToken(metadata?.currentPageToken || '')
                        setNextPageToken(metadata?.nextPageToken || '')
                        // if we are on the first page
                        // (may happen when we reset paging on filter),
                        // empty previousPageTokens
                        if (metadata?.currentPageToken === '') {
                            setPreviousPageTokens([])
                        }
                    }
                    setItemsArray(items.items)
                }
                return getTableContents(itemsArray)
            // no default
        }
    }

    let listTable = (
        <>
            <Grid item className={classes.listContents}>
                <Paper elevation={0}>{contents()}</Paper>
            </Grid>
            {onPageChange && (
                <Pagination
                    type={paginationType}
                    currentPageToken={currentPageToken}
                    previousPageTokens={previousPageTokens}
                    setPreviousPageTokens={setPreviousPageTokens}
                    nextPageToken={nextPageToken}
                    onPageChange={onPageChange}
                />
            )}
        </>
    )

    if (additionalInfo) {
        listTable = (
            <Box display="flex" flexDirection="row" className={classes.boxListContents}>
                <Box display="flex" flexGrow={2} flexDirection="column">
                    <Paper elevation={0}>{contents()}</Paper>
                    {onPageChange && (
                        <Pagination
                            type={paginationType}
                            currentPageToken={currentPageToken}
                            previousPageTokens={previousPageTokens}
                            setPreviousPageTokens={setPreviousPageTokens}
                            nextPageToken={nextPageToken}
                            onPageChange={onPageChange}
                        />
                    )}
                </Box>
                <Box display="flex" flexGrow={1} className={classes.additionalInfo}>
                    {additionalInfo}
                </Box>
            </Box>
        )
    }

    return listTable
}

function List<T>({
    title,
    subtitle,
    emptyText,
    emptyTextTooltip,
    titleVariant,
    listVariant,
    items,
    header,
    search,
    buttons,
    renderItem,
    onPageChange,
    additionalInfo,
    paginationType = PaginationType.paged,
    table,
}: ListProps<T>): React.ReactElement {
    const theme = useTheme()
    const classes = useStyles(theme)

    // Button placement depends on what else is visible.
    let buttonsInTitle = false
    let buttonsInSearch = false
    let hasButtons = false
    // Buttons can either be not set, an array of jsx elements, or a jsx element
    if (buttons) {
        hasButtons = (buttons as React.ReactElement[]).length !== 0
    }
    if (hasButtons) {
        if (title || subtitle) {
            // If there's a title or a subtitle the buttons go in the title area
            buttonsInTitle = true
        } else if (!search) {
            // If there's no search, the buttons go into the title area
            buttonsInTitle = true
        } else {
            // If there's search and no title or subtitle the buttons go into the search area
            buttonsInSearch = true
        }
    }

    const titleStyle = clsx({
        [classes.underline]: listVariant !== 'simple',
        [classes.simpleTitle]: listVariant === 'simple',
    })

    return <>
        {(title || subtitle || buttons) && <Grid container direction="column">
            {/* If we have a title and buttons, show us the buttons up in the title */}
            {title && buttonsInTitle && (
                <Grid container item direction="row" justifyContent="space-between" className={titleStyle} alignItems="center">
                    <Grid item>
                        {titleVariant ? (
                            <Typography variant={titleVariant}>
                                {title}
                                {emptyTextTooltip && emptyText && (
                                    <Tooltip title={emptyText} placement="top">
                                        <C1Icon icon={CircleHelp} className={classes.questionMark} />
                                    </Tooltip>
                                )}
                            </Typography>
                        ) : (
                            <Title>
                                {title}
                                {emptyTextTooltip && emptyText && (
                                    <Tooltip title={emptyText} placement="top">
                                        <C1Icon icon={CircleHelp} className={classes.questionMark} />
                                    </Tooltip>
                                )}
                            </Title>
                        )}
                    </Grid>

                    <Grid item xs>
                        <Grid container direction={listVariant === 'simple' ? 'row' : 'row-reverse'}>
                            {Array.isArray(buttons) &&
                                buttons.map((b, i) => {
                                    return (
                                        <Grid item key={`button_grid_item_${i}`} className={classes.button}>
                                            {b}
                                        </Grid>
                                    )
                                })}
                            {!Array.isArray(buttons) && <Grid item>{buttons}</Grid>}
                        </Grid>
                    </Grid>
                </Grid>
            )}
            {/* If we have a title and no buttons, just show us the title */}
            {title && !buttons && (
                <Grid container item direction="row" justifyContent="space-between" className={titleStyle}>
                    <Grid item>
                        {titleVariant ? (
                            <Typography variant={titleVariant}>
                                {title}
                                {emptyTextTooltip && emptyText && (
                                    <Tooltip title={emptyText} placement="top">
                                        <C1Icon icon={CircleHelp} className={classes.questionMark} />
                                    </Tooltip>
                                )}
                            </Typography>
                        ) : (
                            <Title>
                                {title}
                                {emptyTextTooltip && emptyText && (
                                    <Tooltip title={emptyText} placement="top">
                                        <C1Icon icon={CircleHelp} className={classes.questionMark} />
                                    </Tooltip>
                                )}
                            </Title>
                        )}
                    </Grid>
                </Grid>
            )}
            {/* If we have a title and a subtitle, if there were buttons they're already displayed, so only show the subtitle */}
            {title && subtitle && (
                <Grid item className={classes.margin}>
                    <Subtitle>{subtitle}</Subtitle>
                </Grid>
            )}
            {/* If there's no title and a subtitle, we might have to show buttons, so do that here if buttons exists */}
            {!title && buttonsInTitle && (
                <Grid container direction="row">
                    <Grid item className={classes.margin} xs={buttons ? 9 : 12}>
                        <Subtitle>{subtitle}</Subtitle>
                    </Grid>
                    {buttons && (
                        <Grid container item direction="row-reverse" xs={3}>
                            {Array.isArray(buttons) &&
                                buttons.map((b, i) => {
                                    return (
                                        <Grid item key={`button_grid_item_${i}`} className={classes.button}>
                                            {b}
                                        </Grid>
                                    )
                                })}
                            {!Array.isArray(buttons) && buttons}
                        </Grid>
                    )}
                </Grid>
            )}
        </Grid>}

        {search && <Grid container mt={(title || subtitle || buttons) ? 1 : 0}>
            {/* If there's search and no buttons to show, just show search */}
            {search && !buttonsInSearch && (
                <Grid container item spacing={1} direction="row" className={classes.searchContainer}>
                    {search}
                </Grid>
            )}
            {/* If there's search and buttons and buttons haven't been previously rendered, show buttons in search */}
            {search && buttonsInSearch && (
                <Stack direction="row" spacing={1} alignItems="flex-end" flexGrow={1}>
                    {/* // FIXME Santhosh Rewrite this block */}
                    <Grid container item spacing={1} direction="row" alignItems="center">
                        {search}
                    </Grid>

                    {Array.isArray(buttons) &&
                        buttons.map((b, i) => {
                            return (
                                <Grid item key={`button_grid_item_${i}`} className={classes.button}>
                                    {b}
                                </Grid>
                            )
                        })}
                    {!Array.isArray(buttons) && <Box>{buttons}</Box>}
                </Stack>
            )}
        </Grid>}
        {
            table
                ? <>{table}</>
                : <ListTable
                    emptyText={emptyText}
                    header={header}
                    listVariant={listVariant}
                    renderItem={renderItem}
                    items={items}
                    onPageChange={onPageChange}
                    paginationType={paginationType}
                    additionalInfo={additionalInfo}
                />
        }
    </>
}

export default List
